/**
 * AuthContext — manages user auth state (null = checking, object = logged in, false = anonymous).
 * Cookies are httponly; we just call /api/auth/me to check.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
 */
import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { apiMe, apiLogin, apiLogout } from "../lib/cloudSync";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = anonymous, object = authed
  const [error, setError] = useState(null);

  const checkSession = useCallback(async () => {
    try {
      const data = await apiMe();
      setUser(data.user || false);
    } catch {
      setUser(false);
    }
  }, []);

  useEffect(() => { checkSession(); }, [checkSession]);

  const login = useCallback(async (email, password) => {
    setError(null);
    try {
      const data = await apiLogin(email, password);
      setUser(data.user);
      return data.user;
    } catch (e) {
      setError(e.message);
      throw e;
    }
  }, []);

  const logout = useCallback(async () => {
    try { await apiLogout(); } catch {}
    setUser(false);
  }, []);

  const value = {
    user,                     // null = loading, false = anonymous, object = authed
    isAuthed: !!(user && user.email),
    isLoading: user === null,
    error,
    login,
    logout,
    refresh: checkSession,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
};
